import type { AwardCriterionDto, AwardSubCriterionDto } from "../../autogen/bff-api";
import { isTiptapContentEmpty } from "../input/TipTap/TipTap";
import { percentWeight } from "./orderOfPriority";

// These derivations must mirror the BFF publish gates exactly — FieldKeys.AwardSubCriteriaWeightsValid,
// AwardSubCriteriaDescribed and AwardCriteriaDescriptionsPresent. A green chip here while the Publish
// button is disabled (or the reverse) is the failure mode to avoid; keep the two in step.

export const SUB_WEIGHT_TOTAL = 100;

export const subWeightTotal = (criterion: AwardCriterionDto): number =>
  (criterion.subCriteria ?? []).reduce((sum, sub) => sum + (sub.weight?.number ?? 0), 0);

// A sub-criterion's share of the TOTAL: the criterion's weight multiplied by the sub-criterion's own.
// Null whenever either side is unset, so a half-filled row shows "—" rather than a misleading 0 %.
export const effectiveWeight = (
  criterion: AwardCriterionDto,
  sub: AwardSubCriterionDto,
): number | null => {
  const parent = percentWeight(criterion.weight);
  const own = sub.weight?.number;
  if (parent == null || own == null) return null;
  return (parent * own) / 100;
};

// Effective weights divide by 100 and can trail several digits (40 % x 33 % = 13.2 %). One decimal is
// enough to read, and a whole number renders without a decimal point.
export const formatWeightPercent = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(1);

// Norwegian typography puts a non-breaking space before the sign, so the number never wraps away from it.
export const formatPercent = (value: number): string => `${formatWeightPercent(value)}\u00A0%`;

export const subWeightsValid = (criterion: AwardCriterionDto): boolean => {
  const subCriteria = criterion.subCriteria ?? [];
  if (subCriteria.length === 0) return true;
  // An unweighted sub-criterion is not "zero": it would be scored in the evaluation matrix and then
  // contribute nothing, so it must not hide behind siblings that already reach 100.
  if (subCriteria.some((sub) => sub.weight?.number == null)) return false;
  return subWeightTotal(criterion) === SUB_WEIGHT_TOTAL;
};

export type SubCriterionStatus = "complete" | "missing-weight" | "missing-description";

export const subCriterionStatus = (sub: AwardSubCriterionDto): SubCriterionStatus => {
  if (sub.weight?.number == null) return "missing-weight";
  if (!sub.name?.trim() || isTiptapContentEmpty(sub.description)) return "missing-description";
  return "complete";
};

export type CriterionStatus =
  | { kind: "complete" }
  | { kind: "missing-description" }
  | { kind: "sub-weights-invalid"; total: number }
  // Sub-criteria are optional, so this is informational rather than a warning — it never blocks
  // publishing, and colouring it as a problem would teach buyers to ignore the chips that do.
  | { kind: "no-sub-criteria" };

export const criterionStatus = (criterion: AwardCriterionDto): CriterionStatus => {
  if (!subWeightsValid(criterion)) {
    return { kind: "sub-weights-invalid", total: subWeightTotal(criterion) };
  }
  if (isTiptapContentEmpty(criterion.description)) return { kind: "missing-description" };
  if ((criterion.subCriteria ?? []).length === 0) return { kind: "no-sub-criteria" };
  return { kind: "complete" };
};
